"use client";

import { useState } from "react";
import Link from "next/link";
import { CaretDown } from "@phosphor-icons/react/dist/ssr";

import { cn } from "@/src/lib/utils";
import { Button } from "@/src/components/ui/Button";
import { Text } from "@/src/components/ui/Typography";
import type { NavDropdownCluster } from "@/src/components/ui/NavDropdown";

interface MobileNavItemProps {
  label: string;
  href: string;
  /** Sub-links, grouped under optional headings (a cluster without one is a plain list). */
  clusters?: NavDropdownCluster[];
  onNavigate: () => void;
  dark?: boolean;
}

export function MobileNavItem({
  label,
  href,
  clusters,
  onNavigate,
  dark,
}: MobileNavItemProps) {
  const [open, setOpen] = useState(false);

  if (!clusters?.length) {
    return (
      <Link
        href={href}
        onClick={onNavigate}
        className={cn("block", dark ? "text-neutral-50" : "text-neutral-700")}
      >
        <Text as="span" size="navLG" className="text-inherit">
          {label}
        </Text>
      </Link>
    );
  }

  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        size="auto"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full justify-between"
      >
        <Text
          as="span"
          size="navLG"
          className={`text-inherit ${dark ? "text-neutral-50" : "text-neutral-700"}`}
        >
          {label}
        </Text>

        <CaretDown
          size={16}
          weight="regular"
          className={cn(
            "transition-transform duration-300",
            open && "rotate-180",
            dark && "fill-neutral-50"
          )}
        />
      </Button>

      <div
        className={cn(
          "grid overflow-hidden transition-all duration-300",
          open
            ? "grid-rows-[1fr] pt-3 opacity-100"
            : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="flex min-h-0 flex-col gap-6">
          {clusters.map((cluster, clusterIndex) => (
            <div key={cluster.heading ?? clusterIndex} className="flex flex-col gap-3">
              {cluster.heading && (
                <div className="flex items-center gap-2">
                  <div aria-hidden className="h-4 w-1 shrink-0 rounded-full bg-brand" />
                  <Text
                    as="span"
                    size="labelLG"
                    className={cn(
                      "uppercase tracking-wider",
                      dark ? "text-neutral-50" : "text-neutral-700",
                    )}
                  >
                    {cluster.heading}
                  </Text>
                </div>
              )}
              {cluster.items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onNavigate}
                  className={cn(
                    cluster.heading && "pl-3",
                    dark ? "text-neutral-300" : "text-neutral-600",
                  )}
                >
                  <Text as="span" size="labelMD" className="text-inherit">
                    {item.label}
                  </Text>
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
